import {
  JsonSchemaProperty,
  RowDataValueType,
  ErrorState,
  RowDataReducerState,
  RowDataReducerAction,
} from "../../../types";
import {
  CUSTOM_MULTI_INPUT_CHIPS_REQUIRED_KEYS,
  CUSTOM_MULTI_INPUT_REQUIRED_KEYS,
  CUSTOM_MULTI_SELECT_REQUIRED_KEYS,
  ROW_DATA_KEYS,
} from "../constants";
import { isNewRecord } from "../helpers";
import {
  validateIfEvalMethodNeeded,
  formatErrorMessage,
  noError,
  validateDateField,
  validateNumber,
  validateString,
  validateUniqueField,
  validateUniqueFieldsCombination,
} from "./validationUtils";
import { defineMessages } from "react-intl";
import { getIntl } from "../../../translate/i18n";

export const validateRowDataInput = (
  propertyKey: string,
  property: JsonSchemaProperty,
  newFieldValue: RowDataValueType,
  rowDataReducerState: RowDataReducerState,
  dispatch?: React.Dispatch<RowDataReducerAction>,
): ErrorState => {
  if (!property) return noError();

  const { format, isUnique, uniqueCombination, evalMethod } = property;
  const { rowData } = rowDataReducerState;
  const isNewRecordFlag = isNewRecord(rowData);

  const messages = defineMessages({
    required: {
      id: "validation.required",
      defaultMessage: "This field is required.",
    },
  });

  if (isFieldRequired(propertyKey, property, newFieldValue)) {
    return formatErrorMessage(getIntl().formatMessage(messages.required));
  }

  const shouldValidateUniqueForExistingRecord =
    !isNewRecordFlag &&
    (property.isUnique === true || property.uniqueCombination) &&
    property.isEditable === undefined;

  if (isNewRecordFlag || shouldValidateUniqueForExistingRecord) {
    const uniqueError = validateIfUniqueNeeded(
      propertyKey,
      isUnique,
      newFieldValue,
      rowDataReducerState,
      isNewRecordFlag,
    );
    if (uniqueError.error) return uniqueError;

    const comboError = validateIfUniqueCombination(
      propertyKey,
      uniqueCombination,
      newFieldValue,
      rowDataReducerState,
    );
    if (comboError.error) return comboError;
  }

  const dateError = validateIfDateFormatNeeded(format, newFieldValue);
  if (dateError.error) return dateError;

  const numberValidation = validateStringOrNumber(
    property,
    newFieldValue as string | number,
  );
  if (numberValidation.error) return numberValidation;

  const customValidation = validateIfEvalMethodNeeded({
    evalMethod,
    propertyKey,
    propertySubKey: undefined,
    rowIndex: undefined,
    newFieldValue,
    selectedTabSectionKey: undefined,
    rowDataReducerState,
    dispatch,
  });
  if (customValidation.error) return customValidation;

  return noError();
};

export const isFieldRequired = (
  propertyKey: string,
  property: JsonSchemaProperty,
  newFieldValue: RowDataValueType,
): boolean => {
  if (!property) return false;

  const { type, items, default: defaultValue } = property;

  // Handle array fields
  if (type === "array" && items?.type === "string") {
    const isCustomRequired =
      CUSTOM_MULTI_SELECT_REQUIRED_KEYS.includes(propertyKey) ||
      CUSTOM_MULTI_INPUT_REQUIRED_KEYS.includes(propertyKey) ||
      CUSTOM_MULTI_INPUT_CHIPS_REQUIRED_KEYS.includes(propertyKey);

    const isArrayEmpty =
      newFieldValue === null ||
      newFieldValue === undefined ||
      (Array.isArray(newFieldValue) && newFieldValue.length === 0);

    return isCustomRequired && isArrayEmpty;
  }

  // Handle primitive fields (string, number, etc.)
  const isPrimitiveEmpty =
    newFieldValue === null ||
    newFieldValue === undefined ||
    newFieldValue === "";

  return isPrimitiveEmpty && defaultValue !== null;
};

const validateStringOrNumber = (
  property: JsonSchemaProperty,
  newFieldValue: string | number,
): ErrorState => {
  if (typeof newFieldValue === "string") {
    return validateString(property, newFieldValue);
  }
  if (typeof newFieldValue === "number") {
    return validateNumber(property, newFieldValue);
  }
  return noError();
};

const validateIfUniqueNeeded = (
  propertyKey: string,
  isUnique: boolean | undefined,
  newFieldValue: RowDataValueType,
  rowDataReducerState: RowDataReducerState,
  isNewRecordFlag: boolean,
): ErrorState => {
  if (!isUnique) return noError();

  if (!isNewRecordFlag) {
    const existingRecordId = rowDataReducerState.rowData?.[ROW_DATA_KEYS.ID] as
      | string
      | undefined;
    const currentRecordFromList = rowDataReducerState.rowDataList.find(
      (record) =>
        String(record[ROW_DATA_KEYS.ID] ?? "") ===
        String(existingRecordId ?? ""),
    );

    const currentFieldValue = (currentRecordFromList?.[propertyKey] ??
      rowDataReducerState.resetState?.rowData?.[propertyKey]) as
      | string
      | number
      | undefined;

    if (currentFieldValue !== undefined) {
      const normalizedCurrent = String(currentFieldValue).toLowerCase();
      const normalizedNext = String(newFieldValue ?? "").toLowerCase();

      if (normalizedCurrent === normalizedNext) {
        return noError();
      }
    }
  }

  return validateUniqueField(
    propertyKey,
    newFieldValue as string | number,
    rowDataReducerState,
  );
};

const validateIfUniqueCombination = (
  propertyKey: string,
  uniqueCombination: string[] | undefined,
  newFieldValue: RowDataValueType,
  rowDataReducerState: RowDataReducerState,
): ErrorState => {
  if (!uniqueCombination) return noError();
  return validateUniqueFieldsCombination(
    propertyKey,
    uniqueCombination,
    newFieldValue as string | number,
    rowDataReducerState,
  );
};

const validateIfDateFormatNeeded = (
  format: string | undefined,
  newFieldValue: RowDataValueType,
): ErrorState => {
  if (!format) return noError();
  return validateDateField(format, newFieldValue as string);
};
